import React, { useEffect, useState } from "react";
import Slider, { SliderTooltip } from "rc-slider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ProjectedBar from "../projectedBar/projectedBar";
import { asNumberString } from "../../utils/formatters";
import { faAdd, faChartLine, faEdit } from "@fortawesome/free-solid-svg-icons";
import AreaChart from "../areaChart/areaChart";
import LoadingAnimation from "../loadingAnimation/loadingAnimation";
import { useFetchGraph } from "./hooks/useFetchGraph";
import "rc-slider/assets/index.css";

const onPaste = (e) => {
  e.preventDefault();
  return false;
};

const onKeyPress = (e) => {
  if (e.which < 48 || e.which > 57) {
    e.preventDefault();
  }
};

const { Handle } = Slider;
const handle = (props) => {
  const { value, dragging, index, ...restProps } = props;
  return (
    <SliderTooltip
      prefixCls="rc-slider-tooltip"
      overlay={value + "%"}
      visible={dragging}
      placement="top"
      key={`slider-${index}`}
    >
      <Handle value={value} {...restProps} />
    </SliderTooltip>
  );
};
const GoalListItem = (props) => {
  const {
    goalData,
    visibleColumns: {
      showIncrease,
      showForecast,
      showNeeded,
      showModifyButton,
      showStaff,
      showLocation
    },
    moduleView,
    selectedView,
    saveGoal,
    isAnimated,
    saveGoalAsValue,
    date
  } = props;

  const currencySymbol = goalData.currencySymbol || "";
  const [sliderValue, setSliderValue] = useState(goalData.goalIncrease);
  const [enterValue, setEnterValue] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [isChartMode, setIsChartMode] = useState(false);
  const [goalActive, setGoalActive] = useState(goalData.active);
  const [usePercent, setUsePercent] = useState(goalData.goalUsesPercent);
  const { graphData = [], graphLoading, fetchGraphData, setHasFetched } = useFetchGraph();

  useEffect(() => {
    getEnterValue();
  }, []);

  const getEnterValue = () => {
    if (usePercent) {
      setEnterValue(howMuchChanged(sliderValue, "percent", 0));
    } else if (goalData.goalValue !== null && goalData.goalValue !== undefined) {
      setEnterValue(goalData.goalValue.toFixed());
    }
  };

  const getNeededValue = () => {
    let parsedEnterValue = parseInt(enterValue);
    if (goalData.currentVal === 0) {
      return parseInt(enterValue);
    } else if (Math.sign(parsedEnterValue - goalData.currentVal) === -1) {
      return 0;
    } else {
      return Math.abs(parsedEnterValue - goalData.currentVal);
    }
  };

  const getIncrease = () => {
    return (+sliderValue).toFixed(1);
  };

  const howMuchChanged = (val, typeOfVal = "percent", fixed) => {
    const prev = goalData.prevPeriodVal;
    if (typeOfVal === "percent") {
      let goal = (prev * val) / 100 + prev;
      return goal.toFixed(fixed);
    } else if (typeOfVal == "val") {
      let increase = getPercentageDiff(val, goalData.isPercentageValue);
      return increase.toFixed(fixed);
    }
  };

  const getPercentageDiff = (value, isPercentageValue) => {
    let enterVal = value || 0;
    let increaseInValue = enterVal - goalData.prevPeriodVal;
    let increaseInPercentage = (increaseInValue * 100) / goalData.prevPeriodVal;
    return isPercentageValue ? increaseInValue : increaseInPercentage;
  };

  const logValue = (value) => {
    setSliderValue(value);
    getEnterValue();
  };

  const onFormChange = (event) => {
    let formEnterValue = event.target.value;
    const limitUpTo100Percent = () => {
      let num = parseInt(formEnterValue, 10);
      return isNaN(num) ? "" : num <= 100 ? num.toString() : enterValue;
    };
    const newEnterValue = goalData.isPercentageValue
      ? limitUpTo100Percent()
      : formEnterValue.length > 7
      ? formEnterValue.slice(0, 7)
      : formEnterValue;
    setEnterValue(newEnterValue);
    setSliderValue(howMuchChanged(newEnterValue, "val", 1));
  };

  const resetGoal = () => {
    setSliderValue(goalData.goalIncrease);
    setEnterValue(goalData.goalValue.toFixed());
    setIsEditMode(false);
  };

  const submitGoal = (active) => {
    saveGoal(goalData.id, sliderValue, active, goalData.goalObjectId);
    setIsEditMode(false);
    setGoalActive(active);
    setEnterValue(enterValue);
    setSliderValue(sliderValue);
    setHasFetched(false);
  };

  const handleSubmit = () => {
    submitGoal(true);
  };

  const handleDeactivate = () => {
    submitGoal(false);
  };

  const submitGoalAsNumber = () => {
    saveGoalAsValue(goalData.id, enterValue, true, goalData.goalObjectId);
    setIsEditMode(false);
    setGoalActive(true);
    setHasFetched(false);
  };

  const editMode = () => {
    setIsChartMode(false);
    setIsEditMode(true);
  };

  const chartMode = () => {
    if (isEditMode) {
      setIsEditMode(false);
      resetGoal();
      setUsePercent(goalData.goalUsesPercent);
    }
    fetchGraphData({ date, moduleView, selectedView, goalData });
    setIsChartMode((prev) => !prev);
  };

  const handleTypeToggle = () => {
    setUsePercent(!usePercent);
  };

  const { isPercentageValue } = goalData;
  const isActiveOrEditable = goalActive || isEditMode;

  return (
    <div
      className={
        "goal-list-wrapper show " +
        (isEditMode ? "editActive" : "") +
        (isChartMode ? "chartActive" : "")
      }
      data-testid="goalListItem"
    >
      <div className="top">
        {showStaff && (
          <div className="staff">
            <p>{goalData.entityName}</p>
          </div>
        )}
        {showLocation && (
          <div className="region">
            <p>{goalData.locationName}</p>
          </div>
        )}
        <div className="site">
          <span className="goal-name">{goalData.title}</span>
        </div>
        {isActiveOrEditable && showIncrease && (
          <div className="increase" data-testid="increase">
            {isFinite(getIncrease()) ? getIncrease() + "%" : "N/A"}
          </div>
        )}
        <div className="goal" data-testid="goal">
          {isActiveOrEditable && (
            <p>
              {isPercentageValue
                ? asNumberString(enterValue) + "%"
                : currencySymbol + asNumberString(enterValue)}
            </p>
          )}
        </div>
        <div className="current">
          {isActiveOrEditable && (
            <>
              {isPercentageValue
                ? Math.round(goalData.currentVal) + "%"
                : currencySymbol + asNumberString(goalData.currentVal.toFixed())}
            </>
          )}
        </div>
        {showForecast && (
          <div className="forecast" data-testid="forecast">
            {!isPercentageValue && isActiveOrEditable && (
              <>
                {currencySymbol}
                {asNumberString(Math.round(goalData.currentValPacing))}
              </>
            )}
          </div>
        )}
        {showNeeded && (
          <div className="needed" data-testid="needed">
            {!isPercentageValue && isActiveOrEditable && (
              <>
                {currencySymbol}
                {asNumberString(getNeededValue(enterValue, goalData.currentVal))}
              </>
            )}
          </div>
        )}
        <div className="likely">
          {!isPercentageValue && isActiveOrEditable && (
            <ProjectedBar
              isAnimated={isAnimated}
              metricTitle=""
              value={goalData.currentVal / enterValue || 0}
              projectedValue={goalData.currentValPacing / enterValue || 0}
              hideMinus
              showForecast={showForecast}
              currentVal={goalData.currentVal}
              currentValPacing={goalData.currentValPacing}
              currencySymbol={goalData.currencySymbol}
              onMinusClick={() => {}}
              isStacked
            />
          )}
        </div>
        <div className="action">
          <button
            className={`hollow-button chart ${isChartMode ? "active" : ""}`}
            data-testid="show-graph-button"
            onClick={chartMode}
          >
            <FontAwesomeIcon className="times-circle" icon={faChartLine} />
          </button>
          {showModifyButton && (
            <button
              className={`hollow-button edit ${isEditMode ? "active" : ""}`}
              data-testid={isEditMode ? "close-modify-button" : "modify-button"}
              onClick={isEditMode ? resetGoal : editMode}
            >
              <FontAwesomeIcon
                className={isEditMode ? "fa-edit" : goalActive ? "fa-edit" : "fa-set"}
                icon={isEditMode ? faEdit : goalActive ? faEdit : faAdd}
              />
            </button>
          )}
        </div>
      </div>
      {isChartMode && (
        <div className="bottom-chart">
          {graphLoading ? (
            <LoadingAnimation customStyle="chart-loading" />
          ) : Array.isArray(graphData) ? (
            <AreaChart
              chartData={graphData}
              width={1200}
              toolTipContent
              currencySymbol={isPercentageValue ? "%" : currencySymbol}
              previousDataKey="goalValue"
              currentDataKey="current"
              percentageBaseKey="goalValue"
              roundPercentage={isPercentageValue}
            />
          ) : (
            <p className="error">Something went wrong.</p>
          )}
        </div>
      )}
      {showModifyButton && isEditMode && (
        <div className="bottom">
          <div className="prev-month">
            <p className="title">LAST MONTH</p>
            <p>
              {currencySymbol}
              {asNumberString(goalData.prevPeriodVal)}
            </p>
          </div>
          <div className="mtd">
            <p className="title">MONTH TO DATE</p>
            <p>
              {currencySymbol}
              {asNumberString(goalData.currentVal)}
            </p>
          </div>
          {isPercentageValue ? (
            ""
          ) : (
            <div className="month-last-year">
              <p className="title">MONTH LAST YEAR</p>
              <p>
                {currencySymbol}
                {asNumberString(goalData.prevYearVal)}
              </p>
            </div>
          )}
          <div className="enter-value">
            <p className="title">
              {isPercentageValue ? (
                "SET GOAL VALUE IN PERCENTS"
              ) : (
                <>
                  SET GOAL VALUE BY
                  <button
                    className={`hollow-button type ${usePercent && "editActive"}`}
                    onClick={handleTypeToggle}
                  >
                    PERCENT
                  </button>
                  <button
                    className={`hollow-button type ${!usePercent && "editActive"}`}
                    onClick={handleTypeToggle}
                  >
                    VALUE
                  </button>
                </>
              )}
            </p>
            {!isPercentageValue && usePercent && (
              <div className="slider">
                <Slider
                  handle={handle}
                  defaultValue={sliderValue}
                  onChange={logValue}
                  onAfterChange={logValue}
                  min={-20}
                  railStyle={{
                    background: "#bdbdbd",
                    height: "1px"
                  }}
                  trackStyle={{
                    background: "red",
                    height: "1px"
                  }}
                  handleStyle={{
                    borderColor: "white",
                    height: 28,
                    width: 28,
                    marginLeft: -14,
                    marginTop: -13,
                    backgroundColor: "white"
                  }}
                />
              </div>
            )}
            {!usePercent && (
              <input
                data-testid="enterValue"
                className="manual-enter-number"
                type="number"
                min="0"
                value={enterValue}
                onChange={onFormChange}
                onPaste={onPaste}
                onKeyPress={onKeyPress}
              />
            )}
          </div>
          <div className="action">
            <div>
              <button
                className="hollow-button save"
                onClick={usePercent ? handleSubmit : submitGoalAsNumber}
              >
                SAVE
              </button>
              {goalActive && (
                <button
                  className="hollow-button deactivate"
                  data-testid="deactivate"
                  onClick={handleDeactivate}
                >
                  DEACTIVATE
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoalListItem;
